// Types
import type { ClassicalGrid } from '@jet-app/music-classical/api/types';

// Constants/Enums
import { StorageKeys } from '@jet-app/music-classical/utilities/storage/StorageKeys';
import { ObjectGraphType } from '../../dependencies';

// Utils
import { transformToGridSection } from '@jet-app/music-classical/utilities/transforms/grid';
import { inject, type ObjectGraph } from '@jet/environment/dependencies';
import {
    makeDefaultAccessoryButton,
    type AccessoryButton,
} from '@jet-app/music-classical/models/section/AccessoryButton';
import { makeCompoundAction } from '@jet-app/music-classical/action-model';
import type { InvalidationAction } from '@jet-app/music-classical/models';
import type { RemoveRecentSearchesAction } from '@jet-app/music-classical/action-model/RemoveRecentSearchAction';

const MAX_ITEMS_SHOWN = 10;

export interface RecentlySearchedItem {
    timestamp: Date;
    meta?: any;
}

function renderRecentlySearchedAccessoryButtons(
    objectGraph: ObjectGraph,
): AccessoryButton[] {
    const storage = inject(ObjectGraphType.storage, objectGraph);
    const localization = inject(ObjectGraphType.localization, objectGraph);

    const removeRecentCatalogSearchesAction = storage.setItemAction(
        StorageKeys.User.RecentlySearchedItems,
        [],
    );
    const invalidationAction: InvalidationAction = {
        actionMetrics: {
            data: [],
            custom: {},
        },
        scope: 'inapp',
        kind: 'recentCatalogSearchesChanged',
        $kind: 'invalidationAction',
    };

    const compoundAction = makeCompoundAction(
        {
            data: [],
            custom: {},
        },
        [removeRecentCatalogSearchesAction, invalidationAction],
    );

    const title = localization.string(
        'ASE.Web.MusicClassicalClientJS.Search.RecentlySearched.Clear',
    );
    return [makeDefaultAccessoryButton('clearButton', title, compoundAction)];
}

export function renderRecentlySearchedSection(objectGraph: ObjectGraph) {
    const localization = inject(ObjectGraphType.localization, objectGraph);
    const storage = inject(ObjectGraphType.storage, objectGraph);
    const recentSearchesPersistentValue = storage.getItem(
        StorageKeys.User.RecentlySearchedItems,
    );
    const recentSearches =
        recentSearchItemsFromPersistenceValue(recentSearchesPersistentValue) ??
        [];
    const component = {
        type: 'recentlySearched',
        items: recentSearches.map((item: RecentlySearchedItem) => item.meta),
    };

    return component.items?.length > 0
        ? transformToGridSection(
              component as unknown as ClassicalGrid,
              'rectangularLinkLockup',
              null,
              objectGraph,
              false,
              localization.string(
                  'ASE.Web.MusicClassicalClientJS.Search.RecentlySearched',
              ),
              {
                  kind: 'default',
                  item: {
                      accessoryButtons:
                          renderRecentlySearchedAccessoryButtons(objectGraph),
                  },
              },
          )
        : null;
}

export function recentSearchItemsFromPersistenceValue(
    value: any,
): RecentlySearchedItem[] {
    const persistentValues = value as any[];
    if (!persistentValues) {
        return [];
    }

    return persistentValues
        ?.map((persistentValue) => {
            const persistentSearchItem = persistentValue;
            if (!persistentSearchItem) {
                return null;
            }

            const timestamp = persistentSearchItem?.timestamp;
            if (!timestamp) {
                return null;
            }

            return {
                timestamp: new Date(Date.parse(timestamp)),
                meta: persistentSearchItem?.meta,
            };
        })
        .filter(Boolean)
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, MAX_ITEMS_SHOWN);
}
